import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CreditCard, ExternalLink, FileText, Receipt,
  TrendingUp, Calendar, ArrowUpRight,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

function formatCurrency(amount: number, currency: string) {
  const locale = currency === "brl" ? "pt-BR" : "en-US";
  const cur = currency.toUpperCase();
  return new Intl.NumberFormat(locale, { style: "currency", currency: cur }).format(amount);
}

function safeDate(ts: number) {
  try {
    return format(new Date(ts), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "—";
  }
}

function safePeriod(start: number, end: number) {
  try {
    const s = format(new Date(start), "dd/MM", { locale: ptBR });
    const e = format(new Date(end), "dd/MM/yyyy", { locale: ptBR });
    return `${s} – ${e}`;
  } catch {
    return "—";
  }
}

export default function AdminPayments() {
  const { data, isLoading } = trpc.salon.listStripeInvoices.useQuery();
  const createPortal = trpc.salon.createBillingPortal.useMutation({
    onSuccess: (d) => { if (d?.url) window.open(d.url, "_blank"); },
  });

  const invoices = data?.invoices ?? [];
  const totalPaid = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const currency = invoices[0]?.currency ?? "brl";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Histórico de Pagamentos</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Faturas pagas da sua assinatura</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => createPortal.mutate({ origin: window.location.origin })}
          disabled={createPortal.isPending}
        >
          <CreditCard className="w-4 h-4 mr-2" />
          {createPortal.isPending ? "Abrindo..." : "Gerenciar assinatura"}
          <ExternalLink className="w-3.5 h-3.5 ml-1.5 text-muted-foreground" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total pago</p>
              {isLoading ? (
                <Skeleton className="h-6 w-24 mt-1" />
              ) : (
                <p className="text-lg font-bold text-foreground">{formatCurrency(totalPaid, currency)}</p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <Receipt className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Faturas pagas</p>
              {isLoading ? (
                <Skeleton className="h-6 w-12 mt-1" />
              ) : (
                <p className="text-lg font-bold text-foreground">{invoices.length}</p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
              <Calendar className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Última fatura</p>
              {isLoading ? (
                <Skeleton className="h-6 w-28 mt-1" />
              ) : (
                <p className="text-lg font-bold text-foreground">
                  {invoices[0] ? safeDate(invoices[0].date) : "—"}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de faturas */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-muted-foreground" />
            Faturas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">Nenhuma fatura encontrada</p>
              <p className="text-sm text-muted-foreground mt-1">
                As faturas pagas aparecerão aqui após o primeiro pagamento.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => createPortal.mutate({ origin: window.location.origin })}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Acessar portal de pagamento
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3.5">
                  <div className="space-y-0.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-foreground">
                        {inv.number ?? inv.id}
                      </span>
                      <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800">
                        Pago
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {inv.description ?? "Assinatura mensal"} · Período: {safePeriod(inv.periodStart, inv.periodEnd)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Emitida em {safeDate(inv.date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-bold text-base text-foreground">
                      {formatCurrency(inv.amount, inv.currency)}
                    </span>
                    <div className="flex gap-1.5">
                      {inv.pdfUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-muted-foreground hover:text-foreground"
                          onClick={() => window.open(inv.pdfUrl!, "_blank")}
                          title="Baixar PDF"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                      )}
                      {inv.hostedUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-muted-foreground hover:text-foreground"
                          onClick={() => window.open(inv.hostedUrl!, "_blank")}
                          title="Ver fatura online"
                        >
                          <ArrowUpRight className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-center text-muted-foreground">
        Para cancelar, alterar plano ou atualizar dados de pagamento, acesse o{" "}
        <button
          className="text-primary hover:underline"
          onClick={() => createPortal.mutate({ origin: window.location.origin })}
        >
          portal de assinatura
        </button>
        .
      </p>
    </div>
  );
}
